import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { find } from 'lodash';
import Modal from 'react-modal';
import { loadItemBuy, loadWatchlistObject, resetBuy } from '../actions';
import { commafyCurrency, getBuyNowText, getConfig } from '../utils/helpers';
import Fees from './common/Fees';
import IDHorizontal from './common/IDHorizontal';
import { ifSalePurchaseDisclaimer } from '../utils/constants';
import { ThirdPartyCrAgree } from './checkboxes';
import { OrderPostSaleInspection } from './checkboxes/orderPostSaleInspection';
import { stockwaveClassNames } from '../utils/widgets/stockwave';

const styles = {
  customStyles: {
    overlay: {
      zIndex: 99,
      backgroundColor: 'rgba(0, 0, 0, .6)',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  body: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    borderColor: '#E5E5E5',
    width: '100%',
    alignSelf: 'center',
    marginTop: 15,
    marginBottom: 15,
  },
  pseudoDropdown: {
    border: '1px solid lightgray',
    borderRadius: 5,
    padding: '6px 16px',
    cursor: 'default',
    color: '#555',
  },
  link: {
    color: '#4183c4',
  },
};

class BuyModal extends Component {
  state = {
    accountList: [],
    selectedAccount: {},

    paymentTypes: [],
    selectedPayment: '',

    isSubmitting: false,
    agreed: !!getConfig('agreeToTermsAtLogin'),
    thirdPartyCrAgreed: false,
    psiOrdered: false,

    hideAccountsWithoutBidderBadges: undefined,
    hideFees: undefined,
    distanceInKM: undefined,
    isIfSaleListing: undefined,
    isReserveDisclaimerAgreed: false,
    isStockwaveEnabled: getConfig('enableStockwave') === true,

    loaded: false,
  };

  onOpen = () => {
    this.loadFeatures();
  };

  loadFeatures = () => {
    // 3rd Party Cr -- Set to 'true' when: 
    //   -> No 3rdParty Cr -OR- config.ignoreThirdPartyCrAgreed is 'true'
    let hasThirdPartyCr = false;
    if (
      this.props.crData?.crType 
      && this.props.crData?.crLink
      && ['4', '5'].includes(this.props.crData?.crType)
    ) hasThirdPartyCr = true;
    const thirdPartyCrAgreed = (getConfig('ignoreThirdPartyCrAgree') === true || !hasThirdPartyCr);
    const mpFeatures = (this.props.marketplaceFeatures.features || '').split(
      ','
    );
    const eventFeatures = this.props.item?.eventFeatures
      ? this.props.item.eventFeatures.split(',')
      : [];
    this.setState(
      {
        hideAccountsWithoutBidderBadges: mpFeatures.includes('469'),
        hideFees: mpFeatures.includes('548'),
        distanceInKM: mpFeatures.includes('389'),
        isIfSaleListing: eventFeatures.includes('53'),
        isReserveDisclaimerAgreed: !eventFeatures.includes('53'),
        thirdPartyCrAgreed,
      },
      () => {
        this.loadAccounts();
      }
    );
  };

  loadAccounts = () => {
    if (this.props.userProfile.user) {
      this.setState(
        prevState => {
          const accountList = (
            this.props.userProfile.user.accountList || []
          ).filter(account => {
            const isBiddingDisabled = account.enableBidding !== true;
            const isPersonalAccount = account.accountNumber.includes('@');
            const missingRequiredBadge =
              this.state.hideAccountsWithoutBidderBadges &&
              account.badgeNumber === null;
            return !(isBiddingDisabled || isPersonalAccount || missingRequiredBadge);
          });

          const selectedAccount =
            prevState.selectedAccount && prevState.selectedAccount.accountId
              ? prevState.selectedAccount
              : accountList[0] || {};

          return { accountList, selectedAccount };
        },
        () => {
          this.loadPayments();
        }
      );
    }
  };

  loadPayments = () => {
    const { selectedAccount } = this.state;
    const paymentTypes = (selectedAccount.paymentTypesArray || []).filter(
      type => !['Pay Later', 'PayLater'].includes(type)
    );
    // paymentTypes.unshift('Pay Later'); // puts Pay Later in every array as first option
    paymentTypes.push('Pay Later') // 2024.7.17 - adding Pay Later as last option

    const selectedPayment =
      paymentTypes.find(
        paymentType => paymentType === selectedAccount.defaultPaymentType
      ) || paymentTypes[0];

    this.setState({ paymentTypes, selectedPayment, loaded: true });
  };

  handleAccountSelect = e => {
    const userId = Number(e.target.value);
    const selectedAccount = find(this.state.accountList, { userId }) || {};

    this.setState({ selectedAccount }, () => {
      this.loadPayments();
    });
  };

  handlePaymentSelect = e => {
    const selectedPayment = e.target.value;

    if (selectedPayment) {
      this.setState({ selectedPayment });
    }
  };

  handleBuy = e => {
    this.setState({ isSubmitting: true }, () => {
      const mpId = getConfig('marketplaceId');
      const itemId = this.props.item.itemId || this.props.item.id;
      const amount = this.props.item.outrightPrice;
      const userId = this.state.selectedAccount.userId;
      const paymentType =
        !this.state.selectedPayment ||
        this.state.selectedPayment === 'Pay Later'
          ? ''
          : this.state.selectedPayment;
      const transportationFeeId = 0;
      const transportFee = 0;

      this.props
        .loadItemBuy(
          mpId,
          itemId,
          amount,
          userId,
          paymentType,
          transportationFeeId,
          transportFee,
          this.state.psiOrdered
        )
        .then(({ response }) => {
          if (response) {
            if (response.wsStatus === 'Success') {
              this.onBuySuccess(response);
            } else {
              this.onBuySuccess(response);
            }
          } else {
            throw new Error('Buy Error');
          }
        })
        .catch(error => this.onBuyError(error));
    });
  };

  onBuySuccess(response) {
    const mpId = getConfig('marketplaceId');
    this.props.loadWatchlistObject(mpId);
    toastr.success(response.wsStatus, response.wsMessage);
    this.onClose();

    this.setState(
      {
        isSubmitting: false,
        agreed: !!getConfig('agreeToTermsAtLogin'),
      },
      () => {
        setTimeout(() => {
          this.props.loadData();
          this.props.resetBuy();
        }, 1000);
      }
    );
  }

  onBuyWarning(response) {
    toastr.warning('Attention', response.wsMessage, { icon: 'warning' });

    this.setState(
      {
        isSubmitting: false,
      },
      () => {
        setTimeout(() => {
          this.props.loadData();
          this.props.resetBuy();
        }, 1000);
      }
    );
  }

  onBuyError(error) {
    console.error(error);
    toastr.error('Error', error.message || 'An unknown error occurred');
    this.onClose();

    this.setState(
      {
        isSubmitting: false,
      },
      () => {
        setTimeout(() => {
          this.props.loadData();
          this.props.resetBuy();
        }, 1000);
      }
    );
  }

  onClose = () => {
    this.setState(
      {
        agreed: !!getConfig('agreeToTermsAtLogin'),
        thirdPartyCrAgreed: false,
        psiOrdered: false
      },
      () => {
        this.props.onClose();
      }
    );
  };

  renderAccountSelect() {
    // if no authorized accounts
    if (!this.state.accountList.length) {
      return (
        <div className="form-group">
          <label htmlFor="buyer-account">Buyer Account</label>
          <div
            style={{
              ...styles.pseudoDropdown,
              color: this.state.loaded ? 'red' : 'transparent',
            }}
          >
            No Authorized Accounts
          </div>
        </div>
      );
    }

    // if single option don't use a dropdown
    if (this.state.accountList.length === 1) {
      const { accountName, city, state } = this.state.selectedAccount;

      return (
        <div className="form-group">
          <label htmlFor="buyer-account">Buyer Account</label>
          <div style={styles.pseudoDropdown}>
            {`${accountName} – ${city}, ${state}`}
          </div>
        </div>
      );
    }

    // if multiple options show dropdown
    const options = (this.state.accountList || []).map((account, index) => {
      const text = `${account.accountName} – ${account.accountNumber} – ${account.city}, ${account.state}`;

      return (
        <option key={index} value={account.userId}>
          {text}
        </option>
      );
    });

    return (
      <div className="form-group">
        <label htmlFor="buyer-account">Buyer Account</label>
        <select
          id="buyer-account"
          className="form-control"
          onChange={this.handleAccountSelect}
          value={this.state.selectedAccount.userId}
        >
          {options}
        </select>
      </div>
    );
  }

  renderPaymentSelect() {
    if (
      getConfig('hideSinglePaymentOption') &&
      this.state.paymentTypes.length <= 2 // if hideSinglePaymentOption feature is enabled you must have more than just Pay Later
    ) {
      return null;
    }

    // if single option don't use a dropdown
    if (this.state.paymentTypes.length < 2) {
      return (
        <div className="form-group">
          <label htmlFor="buyer-account">Payment Type</label>
          <div
            style={{
              ...styles.pseudoDropdown,
              color: this.state.loaded ? '#555' : 'transparent',
            }}
          >
            {this.state.selectedPayment || '-'}
          </div>
        </div>
      );
    }

    // if multiple options show dropdown
    const options = (this.state.paymentTypes || []).map((item, index) => (
      <option key={index} value={item}>
        {item}
      </option>
    ));

    return (
      <div className="form-group">
        <label htmlFor="payment-types">Payment Type</label>
        <select
          id="payment"
          className="form-control"
          onChange={this.handlePaymentSelect}
          value={this.state.selectedPayment}
        >
          {options}
        </select>
      </div>
    );
  }

  renderBasicInfo() {
    const { item } = this.props;
    const name = `${item.year} ${item.make} ${item.model}`;
    const itemLocation =
      item.itemCity && item.itemState
        ? `${item.itemCity}, ${item.itemState}`
        : item.vehicleCity && item.vehicleState
        ? `${item.vehicleCity}, ${item.vehicleState}`
        : '';

    return (
      <div>
        <h4 style={{ margin: '5px 0' }}>{name}</h4>
        <div style={{ margin: '5px 0' }}>
          <IDHorizontal
            vin={item.vin || item.vIN}
            stockNumber={item.stockNumber}
          />
        </div>
        {itemLocation && (
          <div style={{ margin: '5px 0' }}>
            <strong style={{ marginRight: 4 }}>Location:</strong>
            <span>{`${itemLocation}`}</span>
          </div>
        )}
      </div>
    );
  }

  renderBuyNowPrice() {
    const buyNowPrice = this.props.item.outrightPrice || this.props.item.buyNow;
    const buyNowText = getBuyNowText(this.props.item);
    const buyAmountClassName = this.state.isStockwaveEnabled ? stockwaveClassNames.bidBuyAmount : '';

    return (
      <div style={{ margin: '15px 0' }}>
        <h4
          className={buyAmountClassName}
          style={{
            color: buyNowPrice ? '#016b19' : 'transparent', // keeps the spacing
          }}
        >
          {`${buyNowText} Price: ${commafyCurrency(buyNowPrice)}`}
        </h4>
      </div>
    );
  }

  renderFees() {
    if (!this.state.hideFees) {
      return (
        <div>
          {/* <div style={{ margin: '0 5px 15px 5px' }}>
            <strong>* Normal fees apply for simulcast auctions.</strong>
          </div> */}
          <Fees
            amount={this.props.item.outrightPrice}
            type="1"
            itemId={this.props.item.itemId || this.props.item.id}
            userId={this.state.selectedAccount?.userId || ''}
            psiOrdered={this.state.psiOrdered}
          />
        </div>
      );
    }
  }

  renderIfSaleDisclaimer () {
    if (!this.state.isIfSaleListing) return null;
    return (
      <div>
        <div style={{ 
            margin: '0 10px 10px 15px',
            border: '1px groove',
            background: 'rgba(125,125,55,.10)',
          }}
        >
            <strong>{ifSalePurchaseDisclaimer.HEADING}</strong>
            <p style={{marginTop: 5}}>
              {ifSalePurchaseDisclaimer.DISCLAIMER} 
            </p>
        </div>
      </div>
    )
  }

  renderTermsAndConditions() {
    const { customerTermsLink } = this.props;

    return (
      <div className="form-group">
        <input
          type="checkbox"
          id="agree"
          checked={this.state.agreed}
          disabled={getConfig('agreeToTermsAtLogin')}
          onChange={e => {
            this.setState(prevState => ({ agreed: !prevState.agreed }));
          }}
        />
        {customerTermsLink ? (
          <label htmlFor="agree" style={{ marginLeft: 5, color: '#787878' }}>
            I agree to the{' '}
            <a
              href={customerTermsLink}
              target="_blank"
              rel="noreferrer"
              style={styles.link}
            >
              {`${getConfig('shortName')} Terms and Conditions`}
            </a>
          </label>
        ) : (
          <label htmlFor="agree" style={{ marginLeft: 5, color: '#787878' }}>
            I agree to the Terms and Conditions
          </label>
        )}
      </div>
    );
  }

  /**
   * If item has a 3rd party CR (_type "5"_) - render acknowledgement checkbox
   */
  renderThirdPartyCrAgree() {
    // 3rd Party Cr
    return (
      <ThirdPartyCrAgree
        crLink={this.props.crData?.crLink}
        crType={this.props.crData?.crType}
        isChecked={this.state.thirdPartyCrAgreed}
        linkStyle={styles.link}
        onAgreeChange={e => {
          this.setState(prevState => ({ thirdPartyCrAgreed: !prevState.thirdPartyCrAgreed }));
        }}
      />
    )
  }

  renderPostSaleInspection() {
    const { item } = this.props;

    if(item.postInspectionAvailable !== true) {
      return null;
    }

    return (
      <OrderPostSaleInspection
        isChecked={this.state.psiOrdered}
        linkStyle={styles.link}
        onAgreeChange={e => {
          this.setState(prevState => ({ psiOrdered: !prevState.psiOrdered }));
        }}
      />
    )
  }

  renderReserveUnitDisclaimer() {
    if (!this.state.isIfSaleListing) return null;

    return (
      <div className="form-group">
        <input
          type="checkbox"
          id="disclaimer-agree"
          checked={this.state.isReserveDisclaimerAgreed}
          onChange={e => {
            this.setState(prevState => ({ isReserveDisclaimerAgreed: !prevState.isReserveDisclaimerAgreed }));
          }}
        />
          <label 
            htmlFor="disclaimer-agree" 
            className='marketplace-reusable__modal-checkbox_label'
          >
            {ifSalePurchaseDisclaimer.DISCLAIMER_ACKNOWLEDGE}
          </label>
      </div>
    );
  }

  renderDivider() {
    return <hr className='marketplace-reusable__modal-divider' />;
  }

  renderHeader() {
    const headingText = getBuyNowText(this.props.item);
    return (
      <div className='marketplace-reusable__modal-header'>
        <div style={{ fontSize: 24, color: '#535768' }}>
          <span className="fa fa-car" />
          <span style={{ marginLeft: 10 }}>{headingText}</span>
        </div>
        <button
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
          onClick={this.onClose}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    );
  }

  renderFooter() {
    const {
      agreed,
      isReserveDisclaimerAgreed,
      isStockwaveEnabled,
      isSubmitting,

      selectedAccount,
      selectedPayment,
      thirdPartyCrAgreed,
    } = this.state;

    const isDisabled =
      isSubmitting || !agreed || !isReserveDisclaimerAgreed || !selectedAccount.userId || !selectedPayment || !thirdPartyCrAgreed;

    const buttonText = getBuyNowText(this.props.item, true); // "Buy" | "Reserve"
    const buyButtonClassNames = ['btn', 'btn-primary', 'button-action'];
    if (isStockwaveEnabled) {
      buyButtonClassNames.push(stockwaveClassNames.buyButton);
    }

    return (
      <div className='marketplace-reusable__modal-footer'>
        <div />
        <div className='marketplace-reusable__modal-buttons'>
          <button
            className="btn btn-primary button-cancel"
            style={{
              opacity: isSubmitting ? 0.5 : 1,
              minWidth: 100,
              marginRight: 10,
            }}
            disabled={isSubmitting}
            type="button"
            data-dismiss="modal"
            onClick={this.onClose}
          >
            Cancel
          </button>
          <button
            className={buyButtonClassNames.join(' ')}
            style={{
              opacity: isDisabled ? 0.5 : 1,
              minWidth: 100,
            }}
            type="button"
            disabled={isDisabled}
            onClick={this.handleBuy}
          >
            {buttonText}
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.item || !this.props.userProfile.user) {
      return null;
    }

    return (
      <Modal
        className={'marketplace-reusable__modal-content'}
        isOpen={this.props.isOpen}
        style={styles.customStyles}
        contentLabel="Buy Now Modal"
        ariaHideApp={false}
        onAfterOpen={this.onOpen}
      >
        <div className='marketplace-reusable__modal-container'>
          {this.renderHeader()}
          {this.renderDivider()}
          <div className='marketplace-reusable__modal-body' style={styles.body}>
            {this.renderBasicInfo()}
            {this.renderBuyNowPrice()}
            <form>
              {this.renderFees()}
              {this.renderAccountSelect()}
              {this.renderPaymentSelect()}
              {this.renderIfSaleDisclaimer()}
              <div className='marketplace-reusable__modal-agreements'>
                {this.renderPostSaleInspection()}
                {this.renderReserveUnitDisclaimer()}
                {this.renderTermsAndConditions()}
                {this.renderThirdPartyCrAgree()}
              </div>
            </form>
          </div>
          {this.renderDivider()}
          {this.renderFooter()}
        </div>
      </Modal>
    );
  }
}

BuyModal.defaultProps = {
  loadData: () => null,
};

BuyModal.propTypes = {
  crData: PropTypes.object,
  customerTermsLink: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  loadData: PropTypes.func.isRequired,
  modalId: PropTypes.string.isRequired,
  userProfile: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
  const { customerTermsLink, marketplaceFeatures, userProfile } =
    state.entities;
  return { customerTermsLink, marketplaceFeatures, userProfile };
};

export default connect(mapStateToProps, {
  resetBuy,
  loadItemBuy,
  loadWatchlistObject,
})(BuyModal);
